import Head from '@uiw/react-head';
import React from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'product' | 'article';
  price?: string;
  currency?: string;
  availability?: 'in stock' | 'out of stock' | 'pre-order';
  brand?: string;
  category?: string;
  breadcrumbs?: Array<{
    name: string;
    url: string;
  }>;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  price,
  currency = 'GBP',
  availability,
  brand = 'CodedGhost Minis',
  category,
  breadcrumbs,
}) => {
  const fullTitle = `${title} | CodedGhost Minis - Premium 3D Printed Miniatures`;
  const currentUrl =
    url || (typeof window !== 'undefined' ? window.location.href : '');
  const defaultImage = image || '/CG Minis-logo.jpeg';

  // Create structured data for product or organization
  const structuredData = {
    '@context': 'https://schema.org',
    ...(type === 'product'
      ? {
          '@type': 'Product',
          name: title,
          description,
          brand: {
            '@type': 'Brand',
            name: brand,
          },
          image: defaultImage,
          ...(price && {
            offers: {
              '@type': 'Offer',
              price,
              priceCurrency: currency,
              availability:
                availability === 'in stock'
                  ? 'https://schema.org/InStock'
                  : 'https://schema.org/OutOfStock',
            },
          }),
          ...(category && { category }),
        }
      : {
          '@type': 'Organization',
          name: brand,
          description,
          url: currentUrl,
          logo: defaultImage,
        }),
  };

  // Create breadcrumb structured data
  const breadcrumbData = breadcrumbs
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url,
        })),
      }
    : null;

  return (
    <Head>
      <Head.Title>{fullTitle}</Head.Title>
      <Head.Meta name='description' content={description} />
      {keywords && <Head.Meta name='keywords' content={keywords} />}
      <Head.Meta name='robots' content='index, follow' />
      <Head.Meta name='author' content={brand} />

      {/* Canonical URL */}
      {currentUrl && <Head.Link rel='canonical' href={currentUrl} />}

      {/* Open Graph */}
      <Head.Meta property='og:title' content={fullTitle} />
      <Head.Meta property='og:description' content={description} />
      <Head.Meta property='og:type' content={type} />
      {currentUrl && <Head.Meta property='og:url' content={currentUrl} />}
      <Head.Meta property='og:image' content={defaultImage} />
      <Head.Meta property='og:site_name' content={brand} />

      {/* Twitter Card */}
      <Head.Meta name='twitter:card' content='summary_large_image' />
      <Head.Meta name='twitter:title' content={fullTitle} />
      <Head.Meta name='twitter:description' content={description} />
      <Head.Meta name='twitter:image' content={defaultImage} />

      {/* Product specific meta tags */}
      {type === 'product' && price && (
        <>
          <Head.Meta property='product:price:amount' content={price} />
          <Head.Meta property='product:price:currency' content={currency} />
          {availability && (
            <Head.Meta property='product:availability' content={availability} />
          )}
        </>
      )}

      {/* Structured Data */}
      <Head.Meta
        name='application/ld+json'
        content={JSON.stringify(structuredData)}
      />

      {/* Breadcrumb structured data */}
      {breadcrumbData && (
        <Head.Meta
          name='application/ld+json'
          content={JSON.stringify(breadcrumbData)}
        />
      )}
    </Head>
  );
};

export default SEOHead;
